import React from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaMobile, FaServer, FaDatabase, FaLock } from 'react-icons/fa';

const Projects: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const projects = [
    {
      title: 'Digital Letter Issuance System',
      description: 'Developed a web application to simplify the process of issuing introduction letters (surat penghantar) for residents, enabling digitalization across RT, RW, and kelurahan levels, eliminating the need for manual paper-based letters.',
      image: '/digital-letter.jpg',
      technologies: ['Laravel', 'MySQL', 'Bootstrap', 'RESTful API'],
      features: ['Paperless Letter Issuance', 'Resident-Friendly Interface', 'Multi-level Approval (RT/RW/Kelurahan)', 'Digital Record Management'],
      github: 'https://github.com/azkajulda/aplikasi-surat-penghatar-rt-hingga-kecamatan',
      live: '#',
      category: 'Web Application',
      icon: FaServer as any,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: 'API Attendance Kampung Marketer',
      description: 'Created a comprehensive Application Programming Interface with Laravel 5.8, implementing OAuth 2 for secure attendance management in the Kampung Marketer Mobile Application project.',
      image: '/api-attendance.jpg',
      technologies: ['Laravel 5.8', 'OAuth 2', 'RESTful API', 'MySQL', 'Mobile Integration'],
      features: ['Secure Authentication', 'Real-time Attendance', 'Mobile App Integration', 'Data Analytics'],
      github: '#',
      live: 'https://komerce.id/',
      category: 'Backend API',
      icon: FaServer as any,
      color: 'from-red-500 to-pink-500'
    },
    {
      title: 'Pharmacy Website Application',
      description: 'Developed a comprehensive web application that governs the way of sale and purchase at pharmacies using Laravel Framework 5.6, featuring inventory management and customer management.',
      image: '/pharmacy-app.jpg',
      technologies: ['Laravel 5.6', 'PHP', 'MySQL', 'Bootstrap', 'JavaScript'],
      features: ['Inventory Management', 'Sales Tracking', 'Customer Database', 'Report Generation'],
      github: 'https://github.com/azkajulda/Apotek',
      live: '#',
      category: 'Web Application',
      icon: FaDatabase as any,
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'RFID Lock Door with Arduino Uno',
      description: 'Built an innovative IoT solution using Arduino Uno to create a secure RFID-based door locking system with real-time monitoring and access control capabilities.',
      image: '/rfid-lock.jpg',
      technologies: ['Arduino Uno', 'RFID Technology', 'C++', 'Hardware Integration', 'IoT'],
      features: ['RFID Authentication', 'Real-time Monitoring', 'Access Control', 'Hardware Integration'],
      github: '#',
      live: '#',
      category: 'IoT Hardware',
      icon: FaLock as any,
      color: 'from-purple-500 to-indigo-500'
    },
    {
      title: 'Industry Relations Website',
      description: 'Created a comprehensive web application that connects vocational high school students with companies for internship opportunities using CodeIgniter Framework.',
      image: '/industry-relations.jpg',
      technologies: ['CodeIgniter', 'PHP', 'MySQL', 'Bootstrap', 'jQuery'],
      features: ['Student Portal', 'Company Portal', 'Matching System', 'Application Tracking'],
      github: '#',
      live: '#',
      category: 'Web Platform',
      icon: FaServer as any,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Japanese Dictionary Android App',
      description: 'Developed a comprehensive Japanese dictionary Android application with search functionality, pronunciation guides, and learning features for language learners.',
      image: '/japanese-dict.jpg',
      technologies: ['Android Studio', 'Java', 'SQLite', 'Material Design', 'Mobile UI/UX'],
      features: ['Word Search', 'Pronunciation Audio', 'Favorites System', 'Offline Access'],
      github: '#',
      live: '#',
      category: 'Mobile App',
      icon: FaMobile as any,
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section id="projects" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="gradient-text">Featured Projects</span>
          </motion.h2>
          <motion.div
            variants={itemVariants}
            className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto mb-8"
          />
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            A showcase of my recent work and projects that demonstrate my skills and expertise
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <div className={`h-40 sm:h-48 bg-gradient-to-br ${project.color} flex items-center justify-center`}>
                  <project.icon className="text-white text-6xl" />
                </div>
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                <div className="absolute top-4 right-4">
                  <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700">
                    {project.category}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                  {project.title}
                </h3>
                
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Features */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.features.map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Technologies:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <motion.a
                    href={project.github}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-300"
                  >
                    <FaGithub />
                    <span className="text-sm font-medium">Code</span>
                  </motion.a>
                  <motion.a
                    href={project.live}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-300"
                  >
                    <FaExternalLinkAlt />
                    <span className="text-sm font-medium">Live Demo</span>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Interested in Working Together?</h3>
            <p className="text-lg mb-6 opacity-90">
              Let's discuss your next project and bring your ideas to life
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              <span>Get In Touch</span>
              <FaExternalLinkAlt />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;

